import { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchTradingViewSymbol,
  normalizeBaseSymbolForTradingView,
  type TradingViewMatch,
  type TradingViewSymbolOptions,
} from "./tradingViewSymbolSearch";

export interface UseTradingViewSymbolResult {
  tradingViewSymbol: string | null;
  isLoading: boolean;
  isError: boolean;
}

const CACHE_TTL_MS = 5 * 60 * 1000;

// Shared across hook instances so a drawer and the chart widget inside it
// resolve once and always agree on the symbol.
const resolutionCache = new Map<
  string,
  { expiresAt: number; promise: Promise<TradingViewMatch | null> }
>();

function resolveCached(
  cacheKey: string,
  symbol: string,
  options: TradingViewSymbolOptions
): Promise<TradingViewMatch | null> {
  const now = Date.now();
  const cached = resolutionCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.promise;
  }
  const promise = fetchTradingViewSymbol(symbol, options);
  resolutionCache.set(cacheKey, { expiresAt: now + CACHE_TTL_MS, promise });
  promise.catch(() => resolutionCache.delete(cacheKey));
  return promise;
}

/**
 * Resolves a user/base symbol to a TradingView `EXCHANGE:PAIR` string (no React Query — works in Next without a provider).
 *
 * Pass `referencePrice` whenever a price for the token is known so the right
 * market is chosen when several tokens share a ticker. The symbol is resolved
 * again only when a reference price first becomes available, not on every tick.
 */
export function useTradingViewSymbol(
  symbol?: string | null,
  options: TradingViewSymbolOptions = {}
): UseTradingViewSymbolResult {
  const normalizedInput = useMemo(
    () => symbol?.trim().toUpperCase() ?? "",
    [symbol]
  );
  const searchKey = useMemo(
    () =>
      normalizedInput.length > 0
        ? normalizeBaseSymbolForTradingView(normalizedInput)
        : "",
    [normalizedInput]
  );

  const hasReference = Boolean(options.referencePrice && options.referencePrice > 0);
  const rejectAbove = hasReference ? options.rejectAbove : undefined;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const cacheKey = `${searchKey}|${hasReference ? `ref:${rejectAbove ?? "-"}` : "noref"}`;

  const [tradingViewSymbol, setTradingViewSymbol] = useState<string | null>(
    null
  );
  const [resolvedKey, setResolvedKey] = useState(searchKey);
  const [isLoading, setIsLoading] = useState(searchKey.length > 0);
  const [isError, setIsError] = useState(false);
  const hasSymbolRef = useRef(false);
  hasSymbolRef.current = Boolean(tradingViewSymbol);

  if (resolvedKey !== searchKey) {
    setResolvedKey(searchKey);
    setTradingViewSymbol(null);
    setIsLoading(searchKey.length > 0);
    setIsError(false);
  }

  useEffect(() => {
    if (!searchKey) {
      setTradingViewSymbol(null);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    // Re-resolving (a reference price just arrived) keeps the current chart on
    // screen; only a first lookup shows the loading state.
    setIsLoading((loading) => loading || !hasSymbolRef.current);
    setIsError(false);

    resolveCached(cacheKey, normalizedInput, {
      referencePrice: hasReference ? optionsRef.current.referencePrice : null,
      rejectAbove,
    })
      .then((match) => {
        if (cancelled) return;
        if (!match) {
          setTradingViewSymbol(null);
          setIsError(true);
        } else {
          setTradingViewSymbol(`${match.exchange}:${match.symbol}`);
          setIsError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTradingViewSymbol(null);
          setIsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, normalizedInput, searchKey, hasReference, rejectAbove]);

  return {
    tradingViewSymbol,
    isLoading,
    isError,
  };
}
